/**
 * FPS Monitor Overlay — real-time performance HUD for point cloud rendering.
 *
 * WHY: Users with diverse GPU capabilities (integrated → workstation) need visibility
 * into rendering performance to understand loading behavior and make informed choices
 * about quality vs. smoothness trade-offs.
 *
 * LAYOUT:
 *   Row 1 — FPS counter (color-coded: green ≥50, orange ≥30, red <30)
 *   Row 2 — Point budget display (shows adaptive vs. user-set ceiling)
 *   Row 3 — Sparkline chart (5-second rolling FPS history)
 *   Row 4 — Max Detail toggle (ON = never reduce budget during loading)
 *   Row 5 — Boost toggle (ON = 2× download concurrency + GPU upload rate)
 *   Row 6 — Detected GPU tier + model name
 *
 * @see mainExtension.js — creates this overlay and wires callbacks
 * @see potree.js — setLoadingSpeed() controls the download/GPU pipeline
 */

export default class FPSMonitorOverlay {
    constructor(container, onModeChange, onBoostChange) {
        if (!container) return;

        this._el = null;
        this._history = [];
        this._lastUpdate = 0;
        this._isMaxDetail = true;   // default mode
        this._onModeChange = onModeChange || null;
        this._isBoosted = false;
        this._onBoostChange = onBoostChange || null;

        var el = document.createElement('div');
        el.id = 'pc-fps-monitor';
        el.style.cssText = [
            'position:absolute', 'top:12px', 'left:12px', 'z-index:100',
            'background:rgba(15,15,20,0.82)', 'backdrop-filter:blur(10px)',
            'border-radius:10px', 'padding:10px 14px 8px',
            'color:#fff', 'font-family:"Segoe UI",system-ui,-apple-system,sans-serif',
            'pointer-events:none', 'min-width:190px',
            'border:1px solid rgba(255,255,255,0.08)',
            'box-shadow:0 4px 16px rgba(0,0,0,0.35)'
        ].join(';');

        // Row 1: FPS value + label
        var row1 = document.createElement('div');
        row1.style.cssText = 'display:flex;align-items:baseline;gap:6px;margin-bottom:2px';
        var fpsVal = document.createElement('span');
        fpsVal.className = 'pc-fps-val';
        fpsVal.style.cssText = 'font-size:24px;font-weight:700;font-variant-numeric:tabular-nums;line-height:1';
        fpsVal.textContent = '--';
        var fpsLbl = document.createElement('span');
        fpsLbl.style.cssText = 'font-size:10px;color:rgba(255,255,255,0.45);text-transform:uppercase;letter-spacing:0.8px';
        fpsLbl.textContent = 'FPS';
        row1.appendChild(fpsVal);
        row1.appendChild(fpsLbl);

        // Row 2: Budget
        var row2 = document.createElement('div');
        row2.className = 'pc-fps-budget';
        row2.style.cssText = 'font-size:10px;color:rgba(255,255,255,0.4);margin-bottom:8px;font-variant-numeric:tabular-nums';
        row2.textContent = 'Budget: --';

        // Row 3: Canvas sparkline
        var canvas = document.createElement('canvas');
        canvas.className = 'pc-fps-chart';
        canvas.width = 190;
        canvas.height = 36;
        canvas.style.cssText = 'display:block;border-radius:6px;background:rgba(255,255,255,0.04)';

        // Row 4: Max Detail toggle
        var self = this;
        var row4 = document.createElement('div');
        row4.style.cssText = 'display:flex;align-items:center;justify-content:space-between;margin-top:6px;pointer-events:auto;cursor:pointer';

        var label = document.createElement('span');
        label.style.cssText = 'font-size:10px;color:rgba(255,255,255,0.7);user-select:none';
        label.textContent = 'Max Detail';

        var track = document.createElement('span');
        track.style.cssText = 'display:inline-block;width:28px;height:14px;border-radius:7px;background:rgba(76,175,80,0.6);position:relative;transition:background 0.2s ease;flex-shrink:0';

        var circle = document.createElement('span');
        circle.style.cssText = 'display:block;width:10px;height:10px;border-radius:50%;background:#fff;position:absolute;top:2px;left:2px;transform:translateX(14px);transition:transform 0.2s ease;box-shadow:0 1px 3px rgba(0,0,0,0.4)';

        track.appendChild(circle);
        row4.appendChild(label);
        row4.appendChild(track);

        row4.addEventListener('click', function() {
            self._isMaxDetail = !self._isMaxDetail;
            track.style.background = self._isMaxDetail ? 'rgba(76,175,80,0.6)' : 'rgba(255,255,255,0.2)';
            circle.style.transform = self._isMaxDetail ? 'translateX(14px)' : 'translateX(0)';
            label.style.color = self._isMaxDetail ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.35)';
            if (self._onModeChange) self._onModeChange(self._isMaxDetail);
        });

        // Row 5: Boost toggle
        var row5 = document.createElement('div');
        row5.style.cssText = 'display:flex;align-items:center;justify-content:space-between;margin-top:6px;pointer-events:auto;cursor:pointer';

        var boostLabel = document.createElement('span');
        boostLabel.style.cssText = 'font-size:10px;color:rgba(255,255,255,0.35);user-select:none';
        boostLabel.textContent = '\u26A1 Boost';

        var boostTrack = document.createElement('span');
        boostTrack.style.cssText = 'display:inline-block;width:28px;height:14px;border-radius:7px;background:rgba(255,255,255,0.2);position:relative;transition:background 0.2s ease;flex-shrink:0';

        var boostCircle = document.createElement('span');
        boostCircle.style.cssText = 'display:block;width:10px;height:10px;border-radius:50%;background:#fff;position:absolute;top:2px;left:2px;transform:translateX(0);transition:transform 0.2s ease;box-shadow:0 1px 3px rgba(0,0,0,0.4)';

        boostTrack.appendChild(boostCircle);
        row5.appendChild(boostLabel);
        row5.appendChild(boostTrack);

        row5.addEventListener('click', function() {
            self._isBoosted = !self._isBoosted;
            boostTrack.style.background = self._isBoosted ? 'rgba(255,152,0,0.6)' : 'rgba(255,255,255,0.2)';
            boostCircle.style.transform = self._isBoosted ? 'translateX(14px)' : 'translateX(0)';
            boostLabel.style.color = self._isBoosted ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.35)';
            if (self._onBoostChange) self._onBoostChange(self._isBoosted);
        });

        // Row 6: GPU tier info (populated via setGPUTier)
        var row6 = document.createElement('div');
        row6.className = 'pc-fps-gpu-tier';
        row6.style.cssText = 'font-size:9px;color:rgba(255,255,255,0.30);margin-top:8px;text-align:center;letter-spacing:0.5px;border-top:1px solid rgba(255,255,255,0.06);padding-top:6px';
        row6.textContent = '';

        el.appendChild(row1);
        el.appendChild(row2);
        el.appendChild(canvas);
        el.appendChild(row4);
        el.appendChild(row5);
        el.appendChild(row6);
        container.appendChild(el);
        this._el = el;
    }

    get isMaxDetail() { return this._isMaxDetail; }
    get isBoosted() { return this._isBoosted; }

    setGPUTier(tier, renderer) {
        if (!this._el) return;
        var el = this._el.querySelector('.pc-fps-gpu-tier');
        if (!el) return;
        // Show short GPU name — extract just the model from the full renderer string
        var shortName = renderer || '';
        // Clean ANGLE wrapper: "ANGLE (NVIDIA, NVIDIA GeForce RTX 3060 Laptop GPU (...) Direct3D11 ...)"
        var match = shortName.match(/(?:NVIDIA|AMD|Intel)[^,]*?(?:GeForce|Radeon|Arc|UHD|Iris|Quadro|RTX\s*A)\s*[^\s(]+(?:\s+(?:Laptop\s+)?GPU)?/i);
        if (match) shortName = match[0].replace(/NVIDIA\s+NVIDIA/i, 'NVIDIA').trim();
        else if (shortName.length > 30) shortName = shortName.substring(0, 30) + '\u2026';

        var tierUpper = tier.charAt(0).toUpperCase() + tier.slice(1);
        el.textContent = '\uD83D\uDDA5 ' + tierUpper + (shortName ? ' \u00B7 ' + shortName : '');
    }

    update(fps, effectiveBudget, userBudget, isAdapted) {
        if (!this._el) return;

        var now = performance.now();
        // Throttle display updates to every 200ms (chart stays smooth, CPU stays low)
        if (now - this._lastUpdate < 200) return;
        this._lastUpdate = now;

        // Push history sample (keep ~5s at 200ms intervals = 25 samples)
        this._history.push({ fps: fps, budget: effectiveBudget });
        if (this._history.length > 25) this._history.shift();

        // Update FPS number + color
        var fpsVal = this._el.querySelector('.pc-fps-val');
        if (fpsVal) {
            fpsVal.textContent = fps;
            fpsVal.style.color = fps >= 50 ? '#4CAF50' : fps >= 30 ? '#FFA726' : '#ef5350';
        }

        // Update budget text
        var budgetEl = this._el.querySelector('.pc-fps-budget');
        if (budgetEl) {
            var bStr = this._formatBudget(effectiveBudget);
            var uStr = this._formatBudget(userBudget);
            budgetEl.textContent = isAdapted
                ? 'Budget: ' + bStr + ' / ' + uStr + ' (auto)'
                : 'Budget: ' + uStr;
        }

        // Draw chart
        this._drawChart();
    }

    destroy() {
        if (this._el && this._el.parentNode) {
            this._el.parentNode.removeChild(this._el);
        }
        this._el = null;
        this._history = null;
        this._onModeChange = null;
        this._onBoostChange = null;
    }

    _formatBudget(val) {
        if (val >= 1e9) return (val / 1e9).toFixed(1) + 'B';
        if (val >= 1e6) return (val / 1e6).toFixed(1) + 'M';
        if (val >= 1e3) return (val / 1e3).toFixed(0) + 'K';
        return val.toString();
    }

    _drawChart() {
        var canvas = this._el && this._el.querySelector('.pc-fps-chart');
        if (!canvas || !this._history || this._history.length < 2) return;
        var ctx = canvas.getContext('2d');
        var w = canvas.width;
        var h = canvas.height;
        var pts = this._history;
        var maxFPS = 75;
        var pad = 1;

        ctx.clearRect(0, 0, w, h);

        // Reference lines: 60fps (green) and 30fps (orange)
        ctx.setLineDash([3, 3]);
        ctx.lineWidth = 0.5;
        var y60 = pad + (h - 2 * pad) * (1 - 60 / maxFPS);
        ctx.strokeStyle = 'rgba(76,175,80,0.25)';
        ctx.beginPath(); ctx.moveTo(0, y60); ctx.lineTo(w, y60); ctx.stroke();
        var y30 = pad + (h - 2 * pad) * (1 - 30 / maxFPS);
        ctx.strokeStyle = 'rgba(255,167,38,0.25)';
        ctx.beginPath(); ctx.moveTo(0, y30); ctx.lineTo(w, y30); ctx.stroke();
        ctx.setLineDash([]);

        // Build path
        var xStep = w / (25 - 1); // always scale to full 5s width
        var xOff = (25 - pts.length) * xStep; // right-align current data
        ctx.beginPath();
        for (var i = 0; i < pts.length; i++) {
            var x = xOff + i * xStep;
            var clamped = Math.min(pts[i].fps, maxFPS);
            var y = pad + (h - 2 * pad) * (1 - clamped / maxFPS);
            if (i === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
        }

        // Stroke color based on current FPS
        var curFPS = pts[pts.length - 1].fps;
        var lineColor = curFPS >= 50 ? '#4CAF50' : curFPS >= 30 ? '#FFA726' : '#ef5350';
        ctx.strokeStyle = lineColor;
        ctx.lineWidth = 1.5;
        ctx.lineJoin = 'round';
        ctx.stroke();

        // Fill under curve
        var lastX = xOff + (pts.length - 1) * xStep;
        ctx.lineTo(lastX, h);
        ctx.lineTo(xOff, h);
        ctx.closePath();
        var fillAlpha = curFPS >= 50 ? '0.08' : curFPS >= 30 ? '0.10' : '0.12';
        // Hex to rgba fill
        var r = parseInt(lineColor.slice(1, 3), 16);
        var g = parseInt(lineColor.slice(3, 5), 16);
        var b = parseInt(lineColor.slice(5, 7), 16);
        ctx.fillStyle = 'rgba(' + r + ',' + g + ',' + b + ',' + fillAlpha + ')';
        ctx.fill();
    }
}
